import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { AuthService } from '@/core/auth/auth.service';
import { DeclaracaoExteriorMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/declaracao-exterior-medico/declaracao-exterior-medico.service';
import { ReportDeclaracaoExteriorMedicoRequest, ReportDeclaracaoExteriorMedicoResponse, ReportJobCreatedResponse } from '@/core/api/sicom-api/registro/medico/relatorios/declaracao-exterior-medico/declaracao-exterior-medico.model';
import { CorrespondenciaService } from '@/core/api/sicom-api/registro/medico/relatorios/correspondencia/correspondencia.service';
import { CorrespondenciaRequest } from '@/core/api/sicom-api/registro/medico/relatorios/correspondencia/correspondencia.model';
import { DeclaracaoExteriorMedicoJobService } from '@/core/services/declaracao-exterior-medico-job.service';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { SolicitanteSearchResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportDeclaracaoExteriorMedicoRequest;
type Resp = ReportDeclaracaoExteriorMedicoResponse;

/** Controles tipados do formulário da declaração (dados da declaração + assinante). */
interface DeclaracaoExteriorMedicoControls {
    instituicao: FormControl<string>;
    pais: FormControl<string>;
    informacoesComplementares: FormControl<string>;
    numeroEtiqueta: FormControl<string>;
    txObservacao: FormControl<string>;
    idAssinante: FormControl<number | null>;
    assinanteObj: FormControl<SolicitanteSearchResponse | null>;
}

@Component({
    selector: 'app-declaracao-exterior-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, InputTextModule, TextareaModule, SelectEstado, RelatorioContainer, SelectModule, TooltipModule, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './declaracao-exterior-medico.html'
})
/**
 * Relatório PDF assíncrono: Declaração para Apresentação no Exterior (médico).
 * Herda `RelatorioMedicoBase`: o fluxo registra a correspondência (que valida o
 * assinante por tipo em `COR_RL_TIPO_ASSINA`) e, em seguida, exporta o PDF
 * (encadeados em `exportar()`); polling/download/erro e o card "médico selecionado +
 * Alterar" vêm da base. O assinante é o usuário logado (escolhido por UF), como no
 * Certificado de Regularidade / Ofícios — não é digitado nem escolhido da diretoria.
 * Instituição destinatária é obrigatória; país, informações complementares e número
 * da etiqueta são opcionais.
 */
export class ReportDeclaracaoExteriorMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly formBuilder = inject(FormBuilder);
    private readonly declaracaoService = inject(DeclaracaoExteriorMedicoService);
    private readonly correspondenciaService = inject(CorrespondenciaService);
    private readonly usuarioService = inject(UsuarioService);
    private readonly authService = inject(AuthService);
    readonly declaracaoJobService = inject(DeclaracaoExteriorMedicoJobService);

    readonly job$ = this.declaracaoJobService.job$;

    form: FormGroup<DeclaracaoExteriorMedicoControls> = this.formBuilder.group<DeclaracaoExteriorMedicoControls>({
        instituicao: new FormControl('', { nonNullable: true, validators: Validators.required }),
        pais: new FormControl('', { nonNullable: true }),
        informacoesComplementares: new FormControl('', { nonNullable: true }),
        numeroEtiqueta: new FormControl('', { nonNullable: true }),
        txObservacao: new FormControl('', { nonNullable: true }),
        idAssinante: new FormControl<number | null>(null, Validators.required),
        assinanteObj: new FormControl<SolicitanteSearchResponse | null>(null, Validators.required)
    });

    ufAssinante = '';
    ufAssinanteTocada = false;
    ufDestino = '';
    ufDestinoTocada = false;

    readonly assinantes = signal<SolicitanteSearchResponse[]>([]);
    readonly loadingAssinantes = signal(false);
    private usuarioLogadoId = '';
    private usuarioCpf = '';

    protected get jobFacade(): RelatorioJobFacade {
        return this.declaracaoJobService;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe({
            next: (userInfo) => {
                this.usuarioLogadoId = userInfo.userId;
                this.usuarioCpf = userInfo.cpf || '';
                if (this.usuarioCpf) {
                    this.buscarAssinantePorUf('');
                }
            },
            // Toast local NECESSÁRIO: `getUserInfo()` lê claims do token e não passa por HttpClient,
            // então o interceptor global não vê esta falha. Sem isto, o erro seria silencioso.
            error: () =>
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro de autenticação',
                    detail: 'Não foi possível obter os dados do usuário logado.'
                })
        });
    }

    private buscarAssinantePorUf(uf: string): void {
        if (!this.usuarioCpf) return;

        this.loadingAssinantes.set(true);
        this.usuarioService.buscarSolicitante(this.usuarioCpf, uf, 0, 10).subscribe({
            next: (page) => {
                this.assinantes.set(page.content);
                if (page.content.length === 1) {
                    const solicitante = page.content[0];
                    this.ufAssinante = solicitante.uf;
                    this.form.patchValue({ assinanteObj: solicitante, idAssinante: solicitante.idSolicitante });
                }
                this.loadingAssinantes.set(false);
            },
            error: () => this.loadingAssinantes.set(false)
        });
    }

    onAssinanteChange(assinante: SolicitanteSearchResponse | null): void {
        this.ufAssinante = assinante?.uf ?? this.ufAssinante;
        this.form.patchValue({ idAssinante: assinante?.idSolicitante ?? null });
    }

    alterarUfAssinante(uf: string | null): void {
        this.ufAssinante = uf ?? '';
        this.form.patchValue({ assinanteObj: null, idAssinante: null });
        this.assinantes.set([]);
        if (this.ufAssinante) {
            this.buscarAssinantePorUf(this.ufAssinante);
        }
    }

    marcarUfAssinanteTocada(): void {
        this.ufAssinanteTocada = true;
    }

    marcarUfDestinoTocada(): void {
        this.ufDestinoTocada = true;
    }

    /** Monta o request da declaração; retorna null (abortando) se o form ou as UFs estiverem inválidos. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        this.ufAssinanteTocada = true;
        this.ufDestinoTocada = true;
        if (this.form.invalid || !this.ufAssinante || !this.ufDestino) return null;

        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar a declaração.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        const valor = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            instituicao: valor.instituicao.trim(),
            pais: valor.pais.trim() || undefined,
            assinante: valor.assinanteObj?.nmSolicitante ?? '',
            informacoesComplementares: valor.informacoesComplementares.trim() || undefined,
            numeroEtiqueta: valor.numeroEtiqueta.trim() || undefined
        };
    }

    /** Registra a correspondência (valida o assinante por tipo) e exporta o PDF da declaração. */
    protected exportar(request: Req): Observable<HttpResponse<Resp | ReportJobCreatedResponse>> {
        const { txObservacao, idAssinante } = this.form.getRawValue();
        const corrRequest: CorrespondenciaRequest = {
            txObservacao: txObservacao ?? '',
            ufAssinante: this.ufAssinante,
            idAssinante: idAssinante ?? 0,
            dtValidade: this.formatarDataParaBackend(new Date()) ?? '',
            ufDestino: this.ufDestino,
            idEstruturaOrganizacional: 0
        };
        return this.correspondenciaService.gerarCorrespondencia(corrRequest, this.usuarioLogadoId).pipe(switchMap(() => this.declaracaoService.exportarPdf(request)));
    }

    limparFormulario(): void {
        const assinante = this.form.getRawValue().assinanteObj;
        this.form.reset({
            instituicao: '',
            pais: '',
            informacoesComplementares: '',
            numeroEtiqueta: '',
            txObservacao: '',
            idAssinante: null,
            assinanteObj: null
        });
        if (assinante) {
            this.form.patchValue({ assinanteObj: assinante, idAssinante: assinante.idSolicitante });
        }
        this.ufAssinanteTocada = false;
        this.ufDestino = '';
        this.ufDestinoTocada = false;
        this.limparJob();
    }
}
